import { Badge, Button, Card, EmptyState, Input } from '@axioma/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  KeyRound,
  Scale,
  ShieldCheck,
  Trash2,
  Upload,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { OabSetupModal } from '../components/OabSetupModal.js';
import { PageHeader } from '../components/PageHeader.js';
import { api, extractErrorMessage } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface CertificateRow {
  id: string;
  label: string;
  subjectCN: string;
  issuer: string;
  cpf: string | null;
  oabNumber: string | null;
  oabState: string | null;
  notBefore: string;
  notAfter: string;
  expired: boolean;
  daysToExpiry: number;
  useCount: number;
  lastUsedAt: string | null;
  createdAt: string;
}

interface CertificatesResponse {
  kekConfigured: boolean;
  items: CertificateRow[];
}

export function Perfil(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [oabOpen, setOabOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const certs = useQuery({
    queryKey: ['certificates'],
    queryFn: async () => {
      const { data } = await api.get<CertificatesResponse>('/certificates');
      return data;
    },
  });

  return (
    <div>
      <PageHeader
        eyebrow="Configurações"
        title="Perfil profissional"
        description="OAB, certificados digitais e identidade dentro da plataforma."
      />
      <div className="mx-auto max-w-3xl space-y-6 px-8 py-6">
        {/* OAB section */}
        <Card>
          <div className="flex items-start gap-4 px-5 py-5">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-ax-sm border border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.08)] text-ax-aurora">
              <Scale size={18} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-base text-ax-text-primary">OAB</h2>
              <p className="ax-body-sm mt-0.5 text-ax-text-secondary">
                Sua inscrição na OAB destrava captura automática DJEN.
              </p>
              {user?.oabNumber && user?.oabState ? (
                <div className="mt-3 inline-flex items-center gap-2 rounded-ax-sm border border-ax-line bg-ax-night px-3 py-1.5">
                  <CheckCircle2 size={14} className="text-ax-electric" />
                  <span className="font-mono text-sm text-ax-text-primary">
                    OAB {user.oabNumber}/{user.oabState}
                  </span>
                </div>
              ) : (
                <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                  <AlertTriangle size={12} className="text-ax-aurora" /> Não cadastrada
                </p>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setOabOpen(true)}>
              {user?.oabNumber ? 'Alterar' : 'Cadastrar'}
            </Button>
          </div>
        </Card>

        {/* Cofre de Certificados */}
        <Card>
          <div className="border-b border-ax-line px-5 py-4">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-ax-sm border border-[rgba(110,91,255,0.30)] bg-[rgba(110,91,255,0.08)] text-ax-plasma">
                <ShieldCheck size={18} />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-base text-ax-text-primary">
                  Cofre de Certificado Digital
                </h2>
                <p className="ax-body-sm mt-0.5 text-ax-text-secondary">
                  Certificados ICP-Brasil A1 (.pfx) para acesso MNI/eproc/e-SAJ e peticionamento.
                  Cifragem em envelope com AES-256-GCM. Decifragem só ocorre em runtime de
                  assinatura, com log de auditoria por uso.
                </p>
              </div>
              {certs.data?.kekConfigured && (
                <Button
                  variant="signature"
                  size="sm"
                  leftIcon={<Upload size={14} />}
                  onClick={() => setUploadOpen(true)}
                >
                  Subir .pfx
                </Button>
              )}
            </div>
          </div>

          {!certs.data?.kekConfigured && certs.data && (
            <div className="border-b border-ax-line bg-[rgba(255,82,82,0.05)] px-5 py-3">
              <p className="ax-body-sm flex items-start gap-2 text-ax-critical">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  Servidor sem KEK (Key Encryption Key) configurada. Upload desabilitado até DevOps
                  configurar <code className="font-mono">CERT_KEK_BASE64</code>.
                </span>
              </p>
            </div>
          )}

          <div>
            {certs.isLoading ? (
              <div className="px-5 py-6 text-center font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                Carregando…
              </div>
            ) : (certs.data?.items.length ?? 0) === 0 ? (
              <div className="px-5 py-8">
                <EmptyState
                  icon={<KeyRound size={32} />}
                  title="Nenhum certificado armazenado"
                  description="Suba seu certificado A1 (.pfx) para destravar acesso aos autos via MNI/eproc e peticionamento eletrônico."
                />
              </div>
            ) : (
              <ul className="divide-y divide-ax-line">
                {certs.data?.items.map((c) => (
                  <CertificateRowView key={c.id} cert={c} />
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      {oabOpen && (
        <OabSetupModal
          initialNumber={user?.oabNumber}
          initialState={user?.oabState}
          onClose={() => setOabOpen(false)}
        />
      )}
      {uploadOpen && <UploadCertModal onClose={() => setUploadOpen(false)} />}
    </div>
  );
}

function CertificateRowView({ cert }: { cert: CertificateRow }): JSX.Element {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: async () => {
      await api.delete(`/certificates/${cert.id}`, { data: { reason: 'Revogado pelo usuário.' } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['certificates'] }),
  });

  return (
    <li className="px-5 py-4">
      <div className="flex items-start gap-4">
        <div
          className={[
            'mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-ax-sm border',
            cert.expired
              ? 'border-ax-critical bg-[rgba(255,82,82,0.08)] text-ax-critical'
              : cert.daysToExpiry < 30
                ? 'border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.08)] text-ax-aurora'
                : 'border-[rgba(110,91,255,0.30)] bg-[rgba(110,91,255,0.08)] text-ax-plasma',
          ].join(' ')}
        >
          <ShieldCheck size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="font-display text-sm text-ax-text-primary">{cert.label}</p>
            {cert.expired ? (
              <Badge variant="critical" uppercase>
                Expirado
              </Badge>
            ) : cert.daysToExpiry < 30 ? (
              <Badge variant="aurora" uppercase>
                {cert.daysToExpiry}d
              </Badge>
            ) : null}
          </div>
          <p className="ax-body-sm mt-0.5 truncate text-ax-text-secondary">{cert.subjectCN}</p>
          <p className="mt-1 font-mono text-[11px] text-ax-text-tertiary">
            Emissor: {cert.issuer.split(',')[0]?.replace('CN=', '')}
          </p>
          <div className="mt-2 flex flex-wrap gap-3 font-mono text-[11px] text-ax-text-tertiary">
            {cert.cpf && <span>CPF {maskCpf(cert.cpf)}</span>}
            {cert.oabNumber && (
              <span>
                OAB {cert.oabNumber}/{cert.oabState}
              </span>
            )}
            <span>Válido até {new Date(cert.notAfter).toLocaleDateString('pt-BR')}</span>
            {cert.useCount > 0 && <span>{cert.useCount} usos</span>}
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Trash2 size={12} />}
          loading={revoke.isPending}
          onClick={() => {
            if (confirm(`Revogar certificado "${cert.label}"? Esta ação não pode ser desfeita.`)) {
              revoke.mutate();
            }
          }}
        >
          Revogar
        </Button>
      </div>
    </li>
  );
}

function UploadCertModal({ onClose }: { onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [label, setLabel] = useState('');

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Selecione o .pfx');
      const fd = new FormData();
      fd.append('pfx', file);
      fd.append('password', password);
      if (label) fd.append('label', label);
      const { data } = await api.post('/certificates/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['certificates'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-ax-modal flex items-start justify-center bg-black/60 px-4 py-12 backdrop-blur-sm">
      <div className="shadow-ax-xl w-full max-w-lg rounded-ax-lg border border-ax-line bg-ax-graphite">
        <div className="border-b border-ax-line px-6 py-4">
          <p className="ax-eyebrow text-ax-text-tertiary">Cofre · ICP-Brasil A1</p>
          <h2 className="font-display text-lg tracking-tight text-ax-text-primary">
            Subir certificado .pfx
          </h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            upload.mutate();
          }}
          className="space-y-4 px-6 py-5"
        >
          <div className="bg-ax-night/40 rounded-ax-sm border border-ax-line p-3">
            <p className="ax-body-sm text-ax-text-secondary">
              <strong className="text-ax-text-primary">Como funciona:</strong> seu .pfx é cifrado
              com AES-256-GCM (envelope DEK/KEK) antes de tocar o disco. A senha é cifrada
              separadamente. Decifragem só ocorre em runtime de assinatura, com log auditável de
              cada uso. Você pode revogar a qualquer momento.
            </p>
          </div>

          <div>
            <label className="ax-eyebrow mb-1 block text-ax-text-tertiary">Arquivo .pfx</label>
            <input
              ref={fileRef}
              type="file"
              accept=".pfx,.p12"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block w-full font-mono text-[12px] text-ax-text-secondary file:mr-3 file:rounded-ax-sm file:border-0 file:bg-ax-night file:px-3 file:py-1.5 file:font-mono file:text-[12px] file:text-ax-text-primary hover:file:bg-ax-graphite"
            />
            {file && (
              <p className="mt-1 font-mono text-[10px] text-ax-text-tertiary">
                {file.name} · {(file.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>

          <Input
            label="Senha do .pfx"
            type="password"
            mono
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="off"
          />

          <Input
            label="Apelido (opcional)"
            placeholder="Ex.: Cert OAB 2026 - Certisign"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />

          {upload.isError && (
            <p className="text-xs text-ax-critical">{extractErrorMessage(upload.error)}</p>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="ghost" size="md" type="button" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="signature"
              size="md"
              loading={upload.isPending}
              disabled={!file || !password}
              className="flex-1"
              leftIcon={<Upload size={14} />}
            >
              Subir e cifrar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function maskCpf(cpf: string): string {
  if (cpf.length !== 11) return cpf;
  return `${cpf.slice(0, 3)}.***.***-${cpf.slice(9)}`;
}
